import { AssignmentDoc, DateUtils, RelationshipLagCalendar, ResourceDoc, TaskDoc, TaskRelationship } from 'common';
import { Calendar, ScheduleData } from 'src/components';
import { FieldDelta, ScheduleService, TaskDeltas } from 'src/global/service/ScheduleService';
import { DepthFirstGraphSort } from './DepthFirstGraphSort';

export class CpmException extends Error {
   constructor(message?: string) {
      super(message || 'CPM calculation error');
      this.name = 'CpmException';
   }
}

export enum ActivityType {
   TASK_DEPENDENT = 'TASK_DEPENDENT',
   RESOURCE_DEPENDENT = 'RESOURCE_DEPENDENT',
   LEVEL_OF_EFFORT = 'LEVEL_OF_EFFORT',
   START_MILESTONE = 'START_MILESTONE',
   FINISH_MILESTONE = 'FINISH_MILESTONE',
   WBS_SUMMARY = 'WBS_SUMMARY',
   HAMMOCK = 'HAMMOCK',
   START_FLAG = 'START_FLAG',
   FINISH_FLAG = 'FINISH_FLAG',
}

export enum ConstraintType {
   AS_SOON_AS_POSSIBLE = 'AS_SOON_AS_POSSIBLE',
   AS_LATE_AS_POSSIBLE = 'AS_LATE_AS_POSSIBLE',

   START_NO_EARLIER_THAN = 'START_NO_EARLIER_THAN',
   START_NO_LATER_THAN = 'START_NO_LATER_THAN',

   FINISH_NO_EARLIER_THAN = 'FINISH_NO_EARLIER_THAN',
   FINISH_NO_LATER_THAN = 'FINISH_NO_LATER_THAN',

   MUST_START_ON = 'MUST_START_ON',
   MUST_FINISH_ON = 'MUST_FINISH_ON',

   START_ON = 'START_ON',
   FINISH_ON = 'FINISH_ON',
}

export enum ResourceType {
   MATERIAL = "MATERIAL",
   WORK = "WORK",
   COST = "COST",
}

export enum RelationshipType {
   FINISH_START = "FS",
   START_START = "SS",
   FINISH_FINISH = "FF",
   START_FINISH = "SF",
}
export enum PercentCompleteType {
   DURATION = "DURATION",
   PHYSICAL = "PHYSICAL",
   UNITS = "UNITS",
   SCOPE = "SCOPE"
}

export class ScheduleCalculate extends EventTarget {
   static isActivity(task: TaskDoc): boolean {
      const activityType = task.activity_type?.toUpperCase();
      return !(task.summary || activityType === ActivityType.LEVEL_OF_EFFORT || activityType === ActivityType.WBS_SUMMARY);
   }

   private m_schedule: ScheduleData;
   private m_dataDate: Date;
   private m_projectStartDate: Date;
   private m_projectFinishDate: Date;
   private m_twentyFourHourCalendar: Calendar;

   private taskDeltas: TaskDeltas = new TaskDeltas();

   constructor(scheduleData: ScheduleData, startDate: Date, dataDate: Date) {
      super();
      this.m_schedule = scheduleData;
      this.m_dataDate = dataDate;
      this.m_projectStartDate = startDate;
      this.m_twentyFourHourCalendar = ScheduleService.getTwentyFourHourCalendar();
   }

   /**
    * Returns task IDs that would form a cycle if a new link were added to/from the given task.
    * Collects all transitive successors and predecessors (only activity tasks).
    */
   public static findTasksThatWouldCreateCycle(taskUniqueId: number, scheduleData: ScheduleData): Set<number> {
      const taskById = new Map(scheduleData.tasks.map(t => [t.unique_id, t]));
      const task = taskById.get(taskUniqueId);
      if (!task) return new Set();

      const result = new Set<number>();

      const traverse = (
         current: TaskDoc,
         visited: Set<number>,
         getRelatedIds: (t: TaskDoc) => number[]
      ) => {
         if (visited.has(current.unique_id)) return;
         visited.add(current.unique_id);
         for (const id of getRelatedIds(current)) {
            result.add(id);
            const next = taskById.get(id);
            if (next && ScheduleCalculate.isActivity(next)) traverse(next, visited, getRelatedIds);
         }
      };

      const successorIds = (t: TaskDoc) => (t.successors || []).map(r => r.successor_task_unique_id).filter(Boolean) as number[];
      const predecessorIds = (t: TaskDoc) => (t.predecessors || []).map(r => r.predecessor_task_unique_id).filter(Boolean) as number[];

      traverse(task, new Set(), successorIds);
      traverse(task, new Set(), predecessorIds);
      return result;
   }

   public calculate() {
      // Create a temporary ScheduleSnapshot-like object for DepthFirstGraphSort
      const activities = new DepthFirstGraphSort(this.m_schedule.tasks, ScheduleCalculate.isActivity).sort();
      if (activities.length === 0) {
         return;
      }

      this.validateActivities(activities);
      this.clearDates(activities);

      if (this.m_dataDate == null) {
         this.m_dataDate = this.m_projectStartDate;
      } else {
         if (this.m_projectStartDate < this.m_dataDate) {
            this.m_projectStartDate = this.m_dataDate;
         }
      }
      this.forwardPass(activities);

      const mustFinishBy = DateUtils.parseISO(this.m_schedule.schedule.property_values.must_finish_by);
      const earlyFinish = activities.map(task => DateUtils.parseISO(task.early_finish)).sort((a, b) => a.getTime() - b.getTime())[activities.length - 1];

      if (mustFinishBy == null || earlyFinish > mustFinishBy) {
         this.m_projectFinishDate = earlyFinish;
      } else {
         this.m_projectFinishDate = mustFinishBy;
      }

      this.backwardPass(activities);

      // seems to be only about dates on resource assignments
      this.updateDates(activities);

      // levelOfEffortPass();

      // m_file.getChildTasks().forEach(this::rollupDates);
      this.rollupDatesTask(this.m_schedule.treeRoot.doc);
      //his.m_schedule.tasks.forEach(task => this.rollupDatesTask(task));

      this.wbsSummaryPass();
   }

   public previewCPM(root: TaskDoc, depth: number): TaskDeltas {
      if (!root) {
         throw new Error('no root task provided');
      }
      this.taskDeltas.clear();

      const chain = this.getSuccessorChain(root, depth);
      const debug = chain.map(t => ({ unique_id: t.unique_id, parent_unique_id: t.parent_task_unique_id, predecessors: t.predecessors, successors: t.successors }));
      console.log('debug', debug);

      
      const activities = new DepthFirstGraphSort(chain, ScheduleCalculate.isActivity).sort();

      const debug2 = activities.map(t => ({ unique_id: t.unique_id, parent_unique_id: t.parent_task_unique_id, predecessors: t.predecessors, successors: t.successors }));

      if (activities.length > 0 && activities[0].predecessors?.length > 0) {
         // this will not go well
         console.log('predecessors', activities[0].predecessors);
      }

      //this.validateActivities(activities);
      this.forwardPass(activities);
      this.updateDates(activities);

      //const parentContainer = this.m_schedule.tasks.find(task => task.unique_id == root.parent_task_unique_id) || root;
      this.rollupDatesTask(root);
      return this.taskDeltas;
   }

   private getSuccessorChain(root: TaskDoc, maxTasks: number): TaskDoc[] {
      const result: TaskDoc[] = [];
      const seen = new Set<number>();

      const addTask = (t: TaskDoc) => {
         if (seen.has(t.unique_id)) return false;
         seen.add(t.unique_id);
         result.push(t);
         return true;
      };

      const walkSuccessors = (task: TaskDoc, maxTasks: number) => {
         if (result.length >= maxTasks) return;

         for (const rel of this.filterSuccessors(task.successors)) {
            const succ = this.getSuccessorTask(rel);
            if (!succ || !ScheduleCalculate.isActivity(succ)) continue;

            if (addTask(succ)) {
               walkSuccessors(succ, maxTasks - 1);
            }

            if (result.length >= maxTasks) return;
         }
      };

      const walkChildren = (task: TaskDoc) => {
         if (result.length >= maxTasks) return;

         if (task.name.includes('container 5')) {
            console.log('walkChildren', task.name);
         }

         // If this is an activity, it's a seed
         if (ScheduleCalculate.isActivity(task)) {
            if (addTask(task)) walkSuccessors(task, maxTasks);
            return;
         }

         // Otherwise descend into children
         const children = this.getChildTasks(task);
         for (const c of children) {
            walkChildren(c);
            if (result.length >= maxTasks) return;
         }
      };

      walkChildren(root);
      walkSuccessors(root, maxTasks);
      return result;
   }
   /*
      private updateTaskAndSuccessors(task: TaskDoc, depth: number, changes: Map<number, FieldDelta[]>): void {
         if (depth == 0) {
            return;
         }
         //this.clearTaskDates(task);
         const deltas = this.forwardPassTask(task);
         if (deltas.length > 0) {
            changes.get(task.unique_id).push(...deltas);
            this.updateDates(task);
         }
   
         this.filterSuccessors(task.successors).forEach(relation => {
            const successor = this.getSuccessorTask(relation);
            this.updateTaskAndSuccessors(successor, depth - 1, changes);
         });
      }*/

   private getResourceAssignments(task: TaskDoc): AssignmentDoc[] {
      return (this.m_schedule.assignments || []).filter(assignment => assignment.task_unique_id === task.unique_id);
   }

   private getResourcesForTask(task: TaskDoc): ResourceDoc[] {
      const resourceAssignments = this.getResourceAssignments(task);
      return (this.m_schedule.resources || []).filter(resource => resourceAssignments.some(assignment => assignment.resource_unique_id === resource.unique_id));
   }

   private validateActivities(tasks: TaskDoc[]): void {
      for (const task of tasks) {
         if (!task.activity_type) {
            console.error(`Task has no activity type: ${task.name || task._id}`);
            return;
         }

         const activityType = task.activity_type.toUpperCase();
         const resourceAssignments = this.getResourceAssignments(task);

         if (activityType === ActivityType.RESOURCE_DEPENDENT && resourceAssignments.length > 0) {
            if (resourceAssignments.some(r => r.work === null || r.work === undefined)) {
               console.error(`Task has resource assignments without a work value: ${task.name || task._id}`);
            }

            if (resourceAssignments.some(r => r.remaining_work === null || r.remaining_work === undefined)) {
               console.error(`Task has resource assignments without a remaining work value: ${task.name || task._id}`);
            }
         } else {
            if (task.duration === null || task.duration === undefined) {
               console.error(`Task has no duration value: ${task.name || task._id}`);
            }

            if (task.remaining_duration === null || task.remaining_duration === undefined) {
               console.error(`Task has no remaining duration value: ${task.name || task._id}`);
            }
         }
      }
   }

   /**
    * Clear dates ready to be repopulated.
    */
   private clearDates(tasks: TaskDoc[]) {
      for (const task of tasks) {
         this.clearTaskDates(task);
         // Clear the critical flag to force it to be recalculated
         task.critical = null;
      }
   }

   private clearTaskDates(task: TaskDoc): void {
      task.start = null;
      task.finish = null;
      task.early_start = null;
      task.early_finish = null;
      task.late_start = null;
      task.late_finish = null;
      task.remaining_early_start = null;
      task.remaining_early_finish = null;
      task.remaining_late_start = null;
      task.remaining_late_finish = null;
   }

   private useTaskEffectiveCalendar(task: TaskDoc): boolean {
      return task.type != ActivityType.RESOURCE_DEPENDENT || !this.getResourcesForTask(task).find(r => r.type == ResourceType.WORK);
   }

   private getEffectiveCalendar(task: TaskDoc): Calendar {
      return this.m_schedule.getCalendar(task);
   }

   private getDateFromStartAndDuration(task: TaskDoc, date: Date): Date {
      return this.getEffectiveCalendar(task).getDateFromPositiveDuration(date, task.duration);
   }

   private getDateFromFinishAndDuration(task: TaskDoc, date: Date): Date {
      return this.getEffectiveCalendar(task).getDateFromNegativeDuration(date, -task.duration);
   }

   private getNextWorkStart(task: TaskDoc, date: Date): Date {
      return this.getEffectiveCalendar(task).getNextWorkStart(date);
   }

   /**
  * Calculate a date from a finish date minus the task remaining duration.
  *
  * @param task parent task
  * @param date finish date
  * @return finish date minus task remaining duration
  */
   private getDateFromFinishAndRemainingDuration(task: TaskDoc, date: Date): Date {
      return this.getEffectiveCalendar(task).getDate(date, -task.remaining_duration);
   }

   private getDateFromStartAndRemainingDuration(task: TaskDoc, date: Date): Date {
      return this.getEffectiveCalendar(task).getDate(date, task.remaining_duration);
   }

   /**
    * Get the maximum early start date from all predecessor relationships.
    *
    * @param task task to get predecessors from
    * @return maximum early start date
    * @throws CpmException if no valid early start dates found
    */
   private getMaxPredecessorEarlyStart(predecessors: TaskRelationship[], currentTask?: TaskDoc): Date {
      const earlyStartDates = predecessors
         .map(rel => {
            return this.calculateEarlyStart(rel, currentTask);
         })
         .filter(date => date != null);

      if (earlyStartDates.length === 0) {
         throw new CpmException("Missing early start date");
      }

      return new Date(Math.max(...earlyStartDates.map(d => d.getTime())));
   }

   private hasActualDuration(task: TaskDoc): boolean {
      return task.actual_duration != null && task.actual_duration > 0;
   }

   private hasRemainingDuration(task: TaskDoc): boolean {
      return task.remaining_duration != null && task.remaining_duration > 0;
   }

   private getTask(taskUniqueId: number): TaskDoc | null {
      return (this.m_schedule.tasks || []).find(t => t.unique_id === taskUniqueId);
   }

   private filterPredecessors(relations: TaskRelationship[]): TaskRelationship[] {
      return (relations || [])
         .filter(rel => this.getTask(rel.predecessor_task_unique_id || rel.task_unique_id) != null);
   }

   private filterSuccessors(relations: TaskRelationship[]): TaskRelationship[] {
      return (relations || [])
         .filter(rel => this.getTask(rel.successor_task_unique_id || rel.task_unique_id) != null);
   }


   /**
    * Perform the CPM forward pass.
    *
    * @param tasks tasks in order for forward pass
    */
   private forwardPass(tasks: TaskDoc[]): void {
      tasks.forEach(task => this.forwardPassTask(task));
   }

   /**
    * Perform the CPM forward pass for this task.
    *
    * @param task task to schedule
    */
   private forwardPassTask(task: TaskDoc): void {
      const original = { ...task };
      var earlyStart: Date | null = null;
      var earlyFinish: Date | null = null;

      const predecessors: TaskRelationship[] = this.filterPredecessors(task.predecessors);

      if (task.actual_start == null) {
         if (predecessors.length === 0) {
            switch (task.constraint_type) {
               case ConstraintType.START_NO_EARLIER_THAN:
                  earlyStart = DateUtils.parseISO(task.constraint_date);
                  if (earlyStart && earlyStart < this.m_dataDate) {
                     earlyStart = this.m_dataDate;
                  }
                  break;

               case ConstraintType.FINISH_NO_EARLIER_THAN:
                  earlyFinish = DateUtils.parseISO(task.constraint_date);
                  earlyStart = this.getDateFromFinishAndDuration(task, earlyFinish);
                  break;

               default:
                  earlyStart = this.m_projectStartDate;
                  break;
            }
         } else {
            earlyStart = this.getMaxPredecessorEarlyStart(predecessors, task);
         }

         let constraintDate = DateUtils.parseISO(task.constraint_date);
         switch (task.constraint_type) {
            case ConstraintType.START_NO_EARLIER_THAN:
               if (earlyStart && earlyStart < DateUtils.parseISO(task.constraint_date)) {
                  earlyStart = constraintDate;
               }
               break;

            case ConstraintType.FINISH_NO_EARLIER_THAN:
               const earliestStart: Date = this.getDateFromFinishAndDuration(task, constraintDate);
               if (earlyStart && earlyStart < earliestStart) {
                  earlyStart = earliestStart;
               }
               break;

            case ConstraintType.MUST_START_ON:
               earlyStart = constraintDate;
               break;

            case ConstraintType.START_ON:
               if (earlyStart && earlyStart < constraintDate) {
                  earlyStart = this.getNextWorkStart(task, constraintDate);
               }
               break;

            case ConstraintType.MUST_FINISH_ON:
               earlyFinish = constraintDate;
               earlyStart = this.getDateFromFinishAndDuration(task, earlyFinish);
               break;

            case ConstraintType.FINISH_ON:
               const startOn: Date = this.getDateFromFinishAndDuration(task, constraintDate);
               if (startOn && startOn > earlyStart) {
                  earlyFinish = constraintDate;
                  earlyStart = startOn;
               }
               break;

            default:
               {
                  break;
               }
         }

         // Don't adjust early start for a finish milestone
         if (task.activity_type != ActivityType.FINISH_MILESTONE) {
            // Next work start
            earlyStart = this.getNextWorkStart(task, earlyStart);
         }
      } else {
         if (task.actual_finish == null) {
            if (predecessors.length === 0) {
               if (!this.hasActualDuration(task)) {
                  earlyStart = this.getNextWorkStart(task, this.m_dataDate);
                  earlyFinish = this.getDateFromStartAndDuration(task, earlyStart);
               } else {
                  if (this.hasRemainingDuration(task)) {
                     earlyFinish = this.getDateFromStartAndDuration(task, DateUtils.parseISO(task.actual_start));
                     // Sometimes this instead... not sure why?
                     //earlyFinish = getNextWorkStart(task, getDateFromStart(task, task.getActualStart()));
                     earlyStart = this.getDateFromFinishAndRemainingDuration(task, earlyFinish);
                  } else {
                     earlyStart = this.getNextWorkStart(task, this.m_dataDate);
                     earlyFinish = earlyStart;
                  }
               }
            } else {
               const maxEarlyStart = this.getMaxPredecessorEarlyStart(predecessors);
               earlyStart = this.getNextWorkStart(task, maxEarlyStart);
               earlyFinish = this.getDateFromStartAndRemainingDuration(task, earlyStart);
            }
         } else {
            if (predecessors.length === 0) {
               earlyStart = this.m_dataDate;
               earlyFinish = this.m_dataDate;
            } else {
               earlyStart = this.getMaxPredecessorEarlyStart(predecessors, task);
               earlyFinish = this.getDateFromStartAndRemainingDuration(task, earlyStart);
            }
         }
      }

      if (task.external_early_start != null && DateUtils.parseISO(task.external_early_start) > earlyStart) {
         earlyStart = DateUtils.parseISO(task.external_early_start);
         earlyFinish = null;
      }

      if (earlyFinish == null) {
         earlyFinish = task.actual_finish == null ? this.getDateFromStartAndDuration(task, earlyStart) : DateUtils.parseISO(task.actual_finish);
      }

      task.early_start = DateUtils.toIso(earlyStart);
      task.early_finish = DateUtils.toIso(earlyFinish);

      this.setRemainingEarlyDates(task);

      this.registerDeltas(original, task);
   }

   /**
    * Populate the remaining early dates.
    *
    * @param task target task
    */
   private setRemainingEarlyDates(task: TaskDoc): void {
      if (task.actual_finish != null) {
         return;
      }

      var remainingEarlyStart: Date;
      if (task.actual_start == null) {
         remainingEarlyStart = DateUtils.parseISO(task.early_start);
      }
      else {
         if (task.actual_duration != null && task.actual_duration != 0) {
            remainingEarlyStart = DateUtils.parseISO(task.early_start);
         }
         else {
            if ((task.actual_duration == null || task.actual_duration == 0) || DateUtils.parseISO(task.actual_start) > this.m_dataDate) {
               remainingEarlyStart = DateUtils.parseISO(task.early_start);
            }
            else {
               remainingEarlyStart = this.getNextWorkStart(task, this.m_dataDate);
            }
         }
      }

      const remainingEarlyFinish = this.getDateFromStartAndRemainingDuration(task, remainingEarlyStart);
      task.remaining_early_start = DateUtils.toIso(remainingEarlyStart);
      task.remaining_early_finish = DateUtils.toIso(remainingEarlyFinish);
   }

   /**
      * Calculate the early start for the successor task in this relationship.
      *
      * @param relation relationship between two tasks
      * @param currentTask the current task being processed (successor)
      * @return calculated early start date
      */
   private calculateEarlyStart(relation: TaskRelationship, currentTask?: TaskDoc): Date {
      switch (relation.type) {
         case RelationshipType.FINISH_START:
            return this.calculateEarlyStartForFinishStart(relation, currentTask);

         case RelationshipType.START_START:
            return this.calculateEarlyStartForStartStart(relation, currentTask);

         case RelationshipType.FINISH_FINISH:
            return this.calculateEarlyStartForFinishFinish(relation, currentTask);

         case RelationshipType.START_FINISH:
            return this.calculateEarlyStartForStartFinish(relation, currentTask);

         default:
            throw new CpmException("Unsupported relationship type: " + relation.type);
      }
   }

   private calculateEarlyStartForFinishStart(relation: TaskRelationship, currentTask?: TaskDoc): Date {
      const predecessorTask = this.getPredecessorTask(relation);
      const successorTask = this.getSuccessorTask(relation, currentTask);
      const earlyFinish = DateUtils.parseISO(predecessorTask.early_finish);

      if (relation.lag > 0) {
         const test = this.addLag(relation, earlyFinish);
         console.log('test', predecessorTask.name, successorTask.name, predecessorTask.early_finish, relation.lag, DateUtils.toIso(test));
      }

      if (!predecessorTask.actual_start) {
         // Predecessor not started
         if (!successorTask.actual_start) {
            // Successor not started
            if (relation.lag == null || relation.lag == 0) {
               return earlyFinish;
            }

            if (relation.lag > 0) {
               return this.addLag(relation, earlyFinish);
            }

            return this.addLag(relation, earlyFinish);
         }

         // successor started
         if (!successorTask.actual_finish) {
            // successor not finished
            if (relation.lag == 0) {
               return this.addLag(relation, earlyFinish);
            }

            if (relation.lag > 0) {
               return this.addLag(relation, earlyFinish);
            }

            return this.addLag(relation, earlyFinish);
         }

         // successor finished
         if (relation.lag == 0) {
            return this.addLag(relation, earlyFinish);
         }

         if (relation.lag > 0) {
            return this.addLag(relation, earlyFinish);
         }

         return this.addLag(relation, earlyFinish);
      }

      // Predecessor started
      if (predecessorTask.actual_finish) {
         const actualFinish = DateUtils.parseISO(predecessorTask.actual_finish);
         // Predecessor finished
         if (!successorTask.actual_start) {
            // Successor not started
            if (relation.lag == 0) {
               return earlyFinish;
            }

            if (relation.lag > 0) {
               const actualLagDurationInHours = actualFinish > this.m_dataDate ? 0 : this.getLagCalendar(relation).getWork(actualFinish, this.m_dataDate);
               const lagDurationInHours = relation.lag * DateUtils.SECONDS_PER_HOUR;
               if (lagDurationInHours > actualLagDurationInHours) {
                  const remainingLag = lagDurationInHours - actualLagDurationInHours;
                  return this.addLag_i(relation, earlyFinish, remainingLag);
               }
               return earlyFinish;
            }

            return earlyFinish;
         }

         // successor started
         if (!successorTask.actual_finish) {
            // successor not finished
            if (relation.lag == 0) {
               return earlyFinish;
            }

            if (relation.lag > 0) {
               return earlyFinish;
            }

            return earlyFinish;
         }

         // successor finished
         if (relation.lag == 0) {
            return earlyFinish;
         }

         if (relation.lag > 0) {
            return earlyFinish;
         }

         return earlyFinish;
      }

      // Predecessor not finished
      if (!successorTask.actual_start) {
         // Successor not started
         if (relation.lag == 0) {
            return this.getNextWorkStart(successorTask, earlyFinish);
         }

         if (relation.lag > 0) {
            return this.addLag(relation, earlyFinish);
         }

         return this.addLag(relation, earlyFinish);
      }

      // successor started
      if (!successorTask.actual_finish) {
         // successor not finished
         if (relation.lag == 0) {
            return this.addLag(relation, earlyFinish);
         }

         if (relation.lag > 0) {
            return this.addLag(relation, earlyFinish);
         }

         return this.addLag(relation, earlyFinish);
      }

      // successor finished
      if (relation.lag == 0) {
         return this.addLag(relation, earlyFinish);
      }

      if (relation.lag > 0) {
         return this.addLag(relation, earlyFinish);
      }

      return this.addLag(relation, earlyFinish);
   }

   private getPredecessorTask(relation: TaskRelationship): TaskDoc | undefined {
      if (!relation.predecessor_task_unique_id) {
         return undefined;
      }
      return (this.m_schedule.tasks || []).find(t => t.unique_id === relation.predecessor_task_unique_id);
   }

   private getSuccessorTask(relation: TaskRelationship, currentTask?: TaskDoc): TaskDoc | undefined {
      // If successor_task_unique_id is not set, use the current task being processed
      if (!relation.successor_task_unique_id) {
         return currentTask;
      }
      return (this.m_schedule.tasks || []).find(t => t.unique_id === relation.successor_task_unique_id);
   }

   private addLag(relation: TaskRelationship, date: Date): Date {
      return this.addLag_i(relation, date, relation.lag);
   }

   /**
    * Add lag to a date.
    *
    * @param relation parent relation
    * @param date date
    * @param lag lag
    * @return date plus lag
    */
   private addLag_i(relation: TaskRelationship, date: Date, lag: number): Date {
      if (date == null) {
         return null;
      }

      const result = this.getLagCalendar(relation).getDate(date, lag);
      if (lag < 0 && result < this.m_dataDate) {
         return this.m_dataDate;
      }

      return result;
   }

   /**
       * Retrieve the lag calendar to use when adding/removing lag.
       *
       * @param relation parent relation
       * @return lag calendar
       */
   private getLagCalendar(relation: TaskRelationship): Calendar {
      switch (this.m_schedule.schedule.property_values.relationship_lag_calendar) {
         case RelationshipLagCalendar.PREDECESSOR:
            return this.getEffectiveCalendar(this.getPredecessorTask(relation));

         case RelationshipLagCalendar.SUCCESSOR:
            return this.getEffectiveCalendar(this.getSuccessorTask(relation));

         case RelationshipLagCalendar.PROJECT_DEFAULT:
            return this.m_schedule.defaultCalendar;

         case RelationshipLagCalendar.TWENTY_FOUR_HOUR:
         default:
            return this.m_twentyFourHourCalendar;
      }
   }

   private calculateEarlyStartForStartStart(relation: TaskRelationship, currentTask?: TaskDoc): Date {
      const predecessorTask = this.getPredecessorTask(relation);
      const successorTask = this.getSuccessorTask(relation, currentTask);
      const earlyStart = DateUtils.parseISO(predecessorTask.early_start);

      if (!predecessorTask.actual_start) {
         // Predecessor not started
         if (!successorTask.actual_start) {
            // Successor not started
            if (relation.lag == null || relation.lag == 0) {
               // why adjust the next work start with the lag calendar? not sure, but it seems to work ;-)
               return this.getLagCalendar(relation).getNextWorkStart(earlyStart);
            }

            if (relation.lag > 0) {
               return this.addLag(relation, earlyStart);
            }

            return this.addLag(relation, earlyStart);
         }

         // successor started
         if (!successorTask.actual_finish) {
            // successor not finished
            if (relation.lag == 0) {
               return this.addLag(relation, earlyStart);
            }

            if (relation.lag > 0) {
               return this.addLag(relation, earlyStart);
            }

            return this.addLag(relation, earlyStart);
         }

         // successor finished
         if (relation.lag == 0) {
            return this.addLag(relation, earlyStart);
         }

         if (relation.lag > 0) {
            return this.addLag(relation, earlyStart);
         }

         return this.addLag(relation, earlyStart);
      }

      // Predecessor started
      if (predecessorTask.actual_finish) {
         const actualStart = DateUtils.parseISO(predecessorTask.actual_start);
         // Predecessor finished
         if (!successorTask.actual_start) {
            // Successor not started
            if (relation.lag == 0) {
               const calculatedEarlyStart = this.addLag(relation, actualStart);
               if (calculatedEarlyStart < this.m_dataDate) {
                  return earlyStart;
               }
               return calculatedEarlyStart;
            }

            if (relation.lag > 0) {
               const calculatedEarlyStart = this.addLag(relation, actualStart);
               if (calculatedEarlyStart < this.m_dataDate) {
                  return earlyStart;
               }
               return calculatedEarlyStart;
            }

            return this.addLag(relation, actualStart);
         }

         // successor started
         if (!successorTask.actual_finish) {
            // successor not finished
            if (relation.lag == 0) {
               return earlyStart;
            }

            if (relation.lag > 0) {
               const calculatedEarlyStart = this.addLag(relation, actualStart);
               if (calculatedEarlyStart < this.m_dataDate) {
                  return earlyStart;
               }
               return calculatedEarlyStart;
            }

            return earlyStart;
         }

         // successor finished
         if (relation.lag == 0) {
            return earlyStart;
         }

         if (relation.lag > 0) {
            return earlyStart;
         }

         return earlyStart;
      }

      // Predecessor not finished
      if (!successorTask.actual_start) {
         // Successor not started
         const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;
         const actualDurationInHours = (predecessorTask.actual_duration || 0) / DateUtils.SECONDS_PER_HOUR;

         if (actualDurationInHours == 0 || lagDurationInHours <= 0.0) {
            // We have a milestone, or we have no positive lag
            return earlyStart;
         }

         if (actualDurationInHours >= lagDurationInHours) {
            // We have progressed more than the lag
            return earlyStart;
         }

         // We still need to account for some or all of the lag
         const remainingLag = (lagDurationInHours - actualDurationInHours) * DateUtils.SECONDS_PER_HOUR;
         return this.addLag_i(relation, earlyStart, remainingLag);
      }

      // successor started
      if (!successorTask.actual_finish) {
         // successor not finished
         const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;
         if (lagDurationInHours <= 0.0) {
            // We have no positive lag
            return earlyStart;
         }

         const actualDurationInHours = (predecessorTask.actual_duration || 0) / DateUtils.SECONDS_PER_HOUR;
         if (actualDurationInHours >= lagDurationInHours) {
            // We have progressed more than the lag
            return earlyStart;
         }

         // We still need to account for some or all of the lag
         const remainingLag = (lagDurationInHours - actualDurationInHours) * DateUtils.SECONDS_PER_HOUR;
         return this.addLag_i(relation, earlyStart, remainingLag);
      }

      // successor finished
      if (relation.lag == 0) {
         return earlyStart;
      }

      if (relation.lag > 0) {
         return earlyStart;
      }

      return earlyStart;
   }

   private calculateEarlyStartForFinishFinish(relation: TaskRelationship, currentTask?: TaskDoc): Date {
      const predecessorTask = this.getPredecessorTask(relation);
      const successorTask = this.getSuccessorTask(relation, currentTask);
      const earlyFinish = DateUtils.parseISO(predecessorTask.early_finish);

      if (!predecessorTask.actual_start) {
         // Predecessor not started
         if (!successorTask.actual_start) {
            // Successor not started
            if (relation.lag == null || relation.lag == 0) {
               return this.getDateFromFinishAndDuration(successorTask, earlyFinish);
            }

            if (relation.lag > 0) {
               return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
            }

            return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
         }

         // successor started
         if (!successorTask.actual_finish) {
            // successor not finished
            if (relation.lag == 0) {
               return this.getDateFromFinishAndDuration(successorTask, earlyFinish);
            }

            if (relation.lag > 0) {
               return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
            }

            return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
         }

         // successor finished
         if (relation.lag == 0) {
            return this.getDateFromFinishAndDuration(successorTask, earlyFinish);
         }

         if (relation.lag > 0) {
            return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
         }

         return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
      }

      // Predecessor started
      if (predecessorTask.actual_finish) {
         const actualFinish = DateUtils.parseISO(predecessorTask.actual_finish);
         // Predecessor finished
         if (!successorTask.actual_start) {
            // Successor not started
            if (relation.lag == 0) {
               return this.getDateFromFinishAndDuration(successorTask, actualFinish);
            }

            if (relation.lag > 0) {
               const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;
               const actualDurationInHours = (predecessorTask.actual_duration || 0) / DateUtils.SECONDS_PER_HOUR;
               if (lagDurationInHours > actualDurationInHours) {
                  return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, actualFinish));
               }

               return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, this.addLag(relation, earlyFinish)));
            }

            return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, this.addLag(relation, earlyFinish)));
         }

         // successor started
         if (!successorTask.actual_finish) {
            // successor not finished
            if (relation.lag == 0) {
               return this.getDateFromFinishAndDuration(successorTask, actualFinish);
            }

            if (relation.lag > 0) {
               return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, actualFinish));
            }

            return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, actualFinish));
         }

         // successor finished
         if (relation.lag == 0) {
            return this.m_dataDate;
            // but sometimes it is
            // this.getLagCalendar(relation).getNextWorkStart(this.m_dataDate)
            // why? remaining lag maybe?
         }

         if (relation.lag > 0) {
            return this.m_dataDate;
            // but sometimes it is
            // this.getLagCalendar(relation).getNextWorkStart(this.m_dataDate)
            // why? remaining lag maybe?
         }

         return this.m_dataDate;
         // but sometimes it is
         // this.getLagCalendar(relation).getNextWorkStart(this.m_dataDate)
         // why? remaining lag maybe?
      }

      // Predecessor not finished
         if (!successorTask.actual_start) {
         // Successor not started
         if (relation.lag == 0) {
            return this.getDateFromFinishAndDuration(successorTask, earlyFinish);
         }

         if (relation.lag > 0) {
            return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
         }

         return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
      }

      // successor started
      if (!successorTask.actual_finish) {
         // successor not finished
         if (relation.lag == 0) {
            return this.getDateFromFinishAndDuration(successorTask, earlyFinish);
         }

         if (relation.lag > 0) {
            return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
         }

         return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
      }

      // successor finished
      if (relation.lag == 0) {
         return this.getDateFromFinishAndDuration(successorTask, earlyFinish);
      }

      if (relation.lag > 0) {
         return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
      }

      return this.getDateFromFinishAndDuration(successorTask, this.addLag(relation, earlyFinish));
   }

   private calculateEarlyStartForStartFinish(relation: TaskRelationship, currentTask?: TaskDoc): Date {
      const predecessorTask = this.getPredecessorTask(relation);
      const successorTask = this.getSuccessorTask(relation, currentTask);
      const earlyStartDate = DateUtils.parseISO(predecessorTask.early_start);
      let earlyStart: Date;

      if (!predecessorTask.actual_start) {
         // Predecessor not started
         if (!successorTask.actual_start) {
            // Successor not started
            if (relation.lag == null || relation.lag == 0) {
               earlyStart = this.addLag(relation, this.getDateFromFinishAndDuration(successorTask, earlyStartDate));
            } else {
               earlyStart = this.addLag(relation, this.getDateFromFinishAndDuration(successorTask, earlyStartDate));
            }
         } else {
            // successor started
            if (!successorTask.actual_finish) {
               // successor not finished
               const actualStart = DateUtils.parseISO(successorTask.actual_start);
               if (relation.lag == null || relation.lag <= 0) {
                  const earlyFinish = this.getDateFromStartAndDuration(successorTask, actualStart);
                  earlyStart = this.getDateFromFinishAndRemainingDuration(successorTask, earlyFinish);
               } else {
                  const earlyFinish = this.addLag(relation, earlyStartDate);
                  earlyStart = this.getDateFromFinishAndRemainingDuration(successorTask, earlyFinish);
               }
            } else {
               // successor finished
               if (relation.lag == 0) {
                  earlyStart = earlyStartDate;
               } else {
                  earlyStart = this.addLag(relation, earlyStartDate);
               }
            }
         }
      } else {
         // Predecessor started
         if (predecessorTask.actual_finish) {
            // Predecessor finished
            if (!successorTask.actual_start) {
               // Successor not started
               if (relation.lag == 0) {
                  earlyStart = this.addLag(relation, this.getDateFromFinishAndDuration(successorTask, earlyStartDate));
               } else {
                  earlyStart = this.addLag(relation, this.getDateFromFinishAndDuration(successorTask, earlyStartDate));
               }
            } else {
               // successor started
               if (!successorTask.actual_finish) {
                  // successor not finished
                  const actualStart = DateUtils.parseISO(predecessorTask.actual_start);
                  const actualLagDurationInHours = actualStart > this.m_dataDate ? 0 : (this.getLagCalendar(relation).getWork(actualStart, this.m_dataDate) || 0) / DateUtils.SECONDS_PER_HOUR;
                  const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;

                  if (lagDurationInHours > actualLagDurationInHours) {
                     const remainingLag = (lagDurationInHours - actualLagDurationInHours) * DateUtils.SECONDS_PER_HOUR;
                     const earlyFinish = this.addLag_i(relation, earlyStartDate, remainingLag);
                     earlyStart = this.getDateFromFinishAndRemainingDuration(successorTask, earlyFinish);
                  } else {
                     earlyStart = earlyStartDate;
                  }
               } else {
                  // successor finished
                  if (relation.lag == 0) {
                     earlyStart = earlyStartDate;
                  } else {
                     earlyStart = this.addLag(relation, earlyStartDate);
                     if (earlyStart > this.m_dataDate) {
                        earlyStart = this.m_dataDate;
                     }
                  }
               }
            }
         } else {
            // Predecessor not finished
            if (!successorTask.actual_start) {
               // Successor not started
               if (relation.lag == 0) {
                  earlyStart = this.addLag(relation, this.getDateFromFinishAndDuration(successorTask, earlyStartDate));
                  if (earlyStart > this.m_dataDate) {
                     earlyStart = this.m_dataDate;
                  }
               } else {
                  earlyStart = this.addLag(relation, this.getDateFromFinishAndDuration(successorTask, earlyStartDate));
                  if (earlyStart > this.m_dataDate) {
                     earlyStart = this.m_dataDate;
                  }
               }
            } else {
               // successor started
               if (!successorTask.actual_finish) {
                  // successor not finished
                  const actualStart = DateUtils.parseISO(predecessorTask.actual_start);
                  const actualLagDurationInHours = actualStart > this.m_dataDate ? 0 : (this.getLagCalendar(relation).getWork(actualStart, this.m_dataDate) || 0) / DateUtils.SECONDS_PER_HOUR;
                  const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;

                  if (lagDurationInHours > actualLagDurationInHours) {
                     const remainingLag = (lagDurationInHours - actualLagDurationInHours) * DateUtils.SECONDS_PER_HOUR;
                     const earlyFinish = this.addLag_i(relation, earlyStartDate, remainingLag);
                     earlyStart = this.getDateFromFinishAndRemainingDuration(successorTask, earlyFinish);
                  } else {
                     earlyStart = earlyStartDate;
                  }
               } else {
                  if (relation.lag == 0) {
                     // successor finished
                     earlyStart = earlyStartDate;
                  } else {
                     // successor finished
                     earlyStart = this.getNextWorkStart(successorTask, this.m_dataDate);
                  }
               }
            }
         }
      }

      return earlyStart;
   }


   private getMinSuccessorLateFinish(successors: TaskRelationship[]): Date {
      const lateFinishDates = successors
         .map(rel => this.calculateLateFinish(rel))
         .filter(date => date != null);

      if (lateFinishDates.length === 0) {
         throw new CpmException("Missing early start date");
      }

      return new Date(Math.min(...lateFinishDates.map(d => d.getTime())));
   }

   private getEquivalentPreviousWorkFinish(task: TaskDoc, date: Date): Date {
      const calendar = this.getEffectiveCalendar(task);
      const previousWorkFinish = calendar.getPreviousWorkFinish(date);
      if (calendar.getWork(previousWorkFinish, date) == 0) {
         return previousWorkFinish;
      }
      return date;
   }

   private calculateLateFinish(relation: TaskRelationship): Date {
      switch (relation.type) {
         case RelationshipType.START_START:
            return this.adjustLateFinish(relation, this.calculateLateFinishForStartStart(relation));

         case RelationshipType.FINISH_FINISH:
            return this.adjustLateFinish(relation, this.calculateLateFinishForFinishFinish(relation));
            
         case RelationshipType.START_FINISH:
            return this.adjustLateFinish(relation, this.calculateLateFinishForStartFinish(relation));

         case RelationshipType.FINISH_START:
            return this.adjustLateFinish(relation, this.calculateLateStartForFinishStart(relation));
            
         default: throw new CpmException("Unsupported relationship type: " + relation.type);
      }
   }

   /**
    * Calculate the late finish for the predecessor task in a start-start relationship.
    *
    * @param relation relationship between two tasks
    * @return calculated late finish date
    */
   private calculateLateFinishForStartStart(relation: TaskRelationship): Date {

      const predecessorTask = this.getPredecessorTask(relation);
      const successorTask = this.getSuccessorTask(relation);
      const successorLateStart = DateUtils.parseISO(successorTask.late_start);
      const successorLateFinish = DateUtils.parseISO(successorTask.late_finish);

      const predecessorActualStart = DateUtils.parseISO(predecessorTask.actual_start);

      let lateFinish: Date | null = null;

      let lateStart: Date;

      if (predecessorTask.actual_start == null) {
         // Predecessor not started
         if (successorTask.actual_start == null) {
            // Successor not started

            if (relation.lag == 0) {
               lateStart = this.getNextWorkStart(predecessorTask, successorLateStart);
               // Sometimes this - why?
               //lateStart = predecessorTask.getEffectiveCalendar().getPreviousWorkFinish(successorTask.getLateStart());
               lateFinish = this.getDateFromStartAndRemainingDuration(predecessorTask, lateStart);

               // Hmmm... dubious logic. Does this just work for indefensible-tedium or is this general?
               if ((successorTask.successors == null || successorTask.successors.length == 0) && successorLateFinish < lateFinish) {
                  lateFinish = successorLateFinish;
               }
            }
            else {
               if (relation.lag > 0) {
                  lateStart = this.getNextWorkStart(predecessorTask, this.removeLag(relation, successorLateStart));
                  lateFinish = this.getDateFromStartAndRemainingDuration(predecessorTask, lateStart);
               } else {
                  lateStart = this.getNextWorkStart(predecessorTask, this.removeLag(relation, successorLateStart));
                  lateFinish = this.getDateFromStartAndRemainingDuration(predecessorTask, lateStart);
               }
            }
         }
         else {
            // successor started
            if (successorTask.actual_finish == null) {
               // successor not finished
               if (relation.lag == 0) {
                  lateStart = this.removeLag(relation, successorLateStart);
               } else {
                  if (relation.lag > 0) {
                     lateStart = this.removeLag(relation, successorLateStart);
                  } else {
                     lateStart = this.removeLag(relation, successorLateStart);
                  }
               }
            } else {
               // successor finished
               if (relation.lag == 0) {
                  lateStart = this.removeLag(relation, successorLateStart);
               } else {
                  if (relation.lag > 0) {
                     lateStart = this.removeLag(relation, successorLateStart);
                  } else {
                     lateStart = this.removeLag(relation, successorLateStart);
                  }
               }
            }
         }
      }
      else {
         // Predecessor Started
         if (predecessorTask.actual_finish != null) {
            // Predecessor finished
            if (successorTask.actual_start == null) {
               // Successor not started
               if (relation.lag == 0) {
                  lateStart = successorLateStart;
               } else {
                  if (relation.lag > 0) {

                     const actualLagDurationInHours = predecessorActualStart > this.m_dataDate ? 0 : this.getLagCalendar(relation).getWork(predecessorActualStart, this.m_dataDate);
                     const lagDurationInHours = relation.lag * DateUtils.SECONDS_PER_HOUR;

                     if (lagDurationInHours > actualLagDurationInHours) {
                        const remainingLag = lagDurationInHours - actualLagDurationInHours;
                        lateStart = this.removeLag_x(relation, successorLateStart, remainingLag);
                     } else {
                        lateStart = successorLateStart;
                     }
                  } else {
                     lateStart = successorLateStart;
                  }
               }
            }
            else {
               // successor started
               if (successorTask.actual_finish == null) {
                  // successor not finished
                  if (relation.lag == 0) {
                     lateStart = successorLateStart;
                  } else {
                     if (relation.lag > 0) {
                        lateStart = successorLateStart;
                     } else {
                        lateStart = successorLateStart;
                     }
                  }
               }
               else {
                  // successor finished
                  if (relation.lag == 0) {
                     lateStart = successorLateStart;
                  }
                  else {
                     if (relation.lag > 0) {
                        const actualLagDurationInHours = predecessorActualStart > this.m_dataDate ? 0 : this.getLagCalendar(relation).getWork(predecessorActualStart, this.m_dataDate);
                        const lagDurationInHours = relation.lag * DateUtils.SECONDS_PER_HOUR;

                        if (lagDurationInHours > actualLagDurationInHours) {
                           const remainingLag = lagDurationInHours - actualLagDurationInHours;
                           lateStart = this.removeLag_x(relation, successorLateStart, remainingLag);
                        } else {
                           lateStart = successorLateStart;
                        }
                     } else {
                        lateStart = successorLateStart;
                     }
                  }
               }
            }
         }
         else {
            // Predecessor not finished
            if (successorTask.actual_start == null) {
               // Successor not started
               if (relation.lag == 0) {
                  lateStart = successorLateStart;
               }
               else {
                  if (relation.lag > 0) {
                     const actualDurationInHours = predecessorTask.actual_duration * DateUtils.SECONDS_PER_HOUR;
                     const lagDurationInHours = relation.lag * DateUtils.SECONDS_PER_HOUR;

                     if (actualDurationInHours >= lagDurationInHours) {
                        lateStart = successorLateStart;
                     } else {
                        const remainingLag = lagDurationInHours - actualDurationInHours;
                        lateStart = this.removeLag_x(relation, successorLateStart, remainingLag);
                     }
                  } else {
                     lateStart = successorLateStart;
                  }
               }
            }
            else {
               // successor started
               if (successorTask.actual_finish == null) {
                  // successor not finished
                  if (relation.lag == 0) {
                     lateStart = successorLateStart;
                  } else {
                     if (relation.lag > 0) {
                        const actualDurationInHours = predecessorTask.actual_duration * DateUtils.SECONDS_PER_HOUR;
                        const lagDurationInHours = relation.lag * DateUtils.SECONDS_PER_HOUR;

                        if (actualDurationInHours >= lagDurationInHours) {
                           lateStart = successorLateStart;
                        } else {
                           const remainingLag = lagDurationInHours - actualDurationInHours;
                           lateStart = this.removeLag_x(relation, successorLateStart, remainingLag);
                        }
                     } else {
                        lateStart = successorLateStart;
                     }
                  }
               } else {
                  // successor finished
                  if (relation.lag == 0) {
                     lateStart = successorLateStart;
                  } else {
                     if (relation.lag > 0) {
                        lateStart = successorLateStart;
                     } else {
                        lateStart = successorLateStart;
                     }
                  }
               }
            }
         }
      }

      if (lateFinish == null) {
         lateFinish = this.getDateFromStartAndRemainingDuration(predecessorTask, lateStart);
      }

      return lateFinish;

   }

   private calculateLateFinishForFinishFinish(relation: TaskRelationship): Date {
      const predecessorTask = this.getPredecessorTask(relation);
      const successorTask = this.getSuccessorTask(relation);
      const successorLateFinish = DateUtils.parseISO(successorTask.late_finish);

      if (predecessorTask.actual_start == null) {
         // Predecessor not started
         if (successorTask.actual_start == null) {
            // Successor not started
            if (relation.lag == null || relation.lag == 0) {
               return this.removeLag(relation, successorLateFinish);
            }

            if (relation.lag > 0) {
               return this.removeLag(relation, successorLateFinish);
            }

            return this.removeLag(relation, successorLateFinish);
         }

         // successor started
         if (successorTask.actual_finish == null) {
            // successor not finished
            if (relation.lag == 0) {
               return this.removeLag(relation, successorLateFinish);
            }

            if (relation.lag > 0) {
               return this.removeLag(relation, successorLateFinish);
            }

            return this.removeLag(relation, successorLateFinish);
         }

         // successor finished
         if (relation.lag == 0) {
            return this.removeLag(relation, successorLateFinish);
         }

         if (relation.lag > 0) {
            return this.removeLag(relation, successorLateFinish);
         }

         return this.removeLag(relation, successorLateFinish);
      }
      // Predecessor Started
      if (predecessorTask.actual_finish != null) {
         const actualFinish = DateUtils.parseISO(predecessorTask.actual_finish);
         // Predecessor finished
         if (successorTask.actual_start == null) {
            if (relation.lag == 0) {
               return successorLateFinish;
            }

            if (relation.lag > 0) {
               // Successor not started
               const actualLagDurationInHours = actualFinish > this.m_dataDate ? 0 : (this.getLagCalendar(relation).getWork(actualFinish, this.m_dataDate) || 0) / DateUtils.SECONDS_PER_HOUR;
               const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;

               if (lagDurationInHours > actualLagDurationInHours) {
                  const remainingLag = (lagDurationInHours - actualLagDurationInHours) * DateUtils.SECONDS_PER_HOUR;
                  return this.removeLag_x(relation, successorLateFinish, remainingLag);
               }

               return successorLateFinish;
            }

            return successorLateFinish;
         }

         // successor started
         if (successorTask.actual_finish == null) {
            // successor not finished
            if (relation.lag == 0) {
               return successorLateFinish;
            }

            if (relation.lag > 0) {
               return successorLateFinish;
            }

            return successorLateFinish;
         }

         // successor finished
         if (relation.lag == 0) {
            return successorLateFinish;
         }

         if (relation.lag > 0) {
            const actualLagDurationInHours = actualFinish > this.m_dataDate ? 0 : (this.getLagCalendar(relation).getWork(actualFinish, this.m_dataDate) || 0) / DateUtils.SECONDS_PER_HOUR;
            const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;

            if (lagDurationInHours > actualLagDurationInHours) {
               const remainingLag = (lagDurationInHours - actualLagDurationInHours) * DateUtils.SECONDS_PER_HOUR;
               return this.removeLag_x(relation, successorLateFinish, remainingLag);
            }

            return successorLateFinish;
         }

         return successorLateFinish;
      }

      // Predecessor not finished
      if (successorTask.actual_start == null) {
         // Successor not started
         if (relation.lag == 0) {
            return this.removeLag(relation, successorLateFinish);
         }

         if (relation.lag > 0) {
            return this.removeLag(relation, successorLateFinish);
         }

         return this.removeLag(relation, successorLateFinish);
      }

      // successor started
      if (successorTask.actual_finish == null) {
         // successor not finished
         if (relation.lag == 0) {
            return this.removeLag(relation, successorLateFinish);
         }

         if (relation.lag > 0) {
            return this.removeLag(relation, successorLateFinish);
         }

         return this.removeLag(relation, successorLateFinish);
      }

      // successor finished
      if (relation.lag == 0) {
         return this.removeLag(relation, successorLateFinish);
      }

      if (relation.lag > 0) {
         return this.removeLag(relation, successorLateFinish);
      }

      return this.removeLag(relation, successorLateFinish);
   }

   private calculateLateFinishForStartFinish(relation: TaskRelationship): Date {
      const predecessorTask = this.getPredecessorTask(relation);
      const successorTask = this.getSuccessorTask(relation);
      const successorLateFinish = DateUtils.parseISO(successorTask.late_finish);

      if (predecessorTask.actual_start == null) {
         // Predecessor not started
         if (successorTask.actual_start == null) {
            // Successor not started
            if (relation.lag == null || relation.lag == 0) {
               return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
            }

            if (relation.lag > 0) {
               return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
            }

            return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
         }

         // successor started
         if (successorTask.actual_finish == null) {
            // successor not finished
            if (relation.lag == 0) {
               return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
            }

            if (relation.lag > 0) {
               return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
            }

            return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
         }

         // successor finished
         if (relation.lag == 0) {
            return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
         }

         if (relation.lag > 0) {
            return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
         }

         return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
      }

      // Predecessor Started
      if (predecessorTask.actual_finish != null) {
         // Predecessor finished
         if (successorTask.actual_start == null) {
            // Successor not started
            if (relation.lag == 0) {
               return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
            }

            if (relation.lag > 0) {
               return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
            }

            return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
         }

         // successor started
         if (successorTask.actual_finish == null) {
            // successor not finished
            if (relation.lag == 0) {
               return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
            }

            if (relation.lag > 0) {
               return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
            }

            return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
         }

         // successor finished
         if (relation.lag == 0) {
            return successorLateFinish;
         }

         if (relation.lag > 0) {
            return successorLateFinish;
         }

         return successorLateFinish;
      }

      // Predecessor not finished
      if (successorTask.actual_start == null) {
         // Successor not started
         if (relation.lag == 0) {
            return this.getDateFromStartAndRemainingDuration(predecessorTask, successorLateFinish);
         }

         if (relation.lag > 0) {
            return this.getDateFromStartAndRemainingDuration(predecessorTask, successorLateFinish);
         }

         return this.getDateFromStartAndRemainingDuration(predecessorTask, successorLateFinish);
      }

      // successor started
      if (successorTask.actual_finish == null) {
         // successor not finished
         if (relation.lag == 0) {
            return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
         }

         if (relation.lag > 0) {
            return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
         }

         return this.removeLag(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish));
      }

      // successor finished
      if (relation.lag == 0) {
         return this.getDateFromStartAndRemainingDuration(predecessorTask, successorLateFinish);
      }

      if (relation.lag > 0) {
         const actualStart = DateUtils.parseISO(predecessorTask.actual_start);
         const actualLagDurationInHours = actualStart > this.m_dataDate ? 0 : (this.getLagCalendar(relation).getWork(actualStart, this.m_dataDate) || 0) / DateUtils.SECONDS_PER_HOUR;
         const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;

         if (lagDurationInHours > actualLagDurationInHours) {
            const remainingLag = (lagDurationInHours - actualLagDurationInHours) * DateUtils.SECONDS_PER_HOUR;
            return this.removeLag_x(relation, this.getDateFromStartAndDuration(predecessorTask, successorLateFinish), remainingLag);
         }

         return this.getDateFromStartAndRemainingDuration(predecessorTask, successorLateFinish);
      }

      return this.getDateFromStartAndRemainingDuration(predecessorTask, successorLateFinish);
   }


   private calculateLateStartForFinishStart(relation: TaskRelationship): Date {
      const predecessorTask = this.getPredecessorTask(relation);
      const successorTask = this.getSuccessorTask(relation);
      const successorLateStart = DateUtils.parseISO(successorTask.late_start);

      if (predecessorTask.actual_start == null) {
         // Predecessor not started
         if (successorTask.actual_start == null) {
            // Successor not started
            if (relation.lag == null || relation.lag == 0) {
               return this.removeLag(relation, successorLateStart);
            }

            if (relation.lag > 0) {
               return this.removeLag(relation, successorLateStart);
            }

            return this.removeLag(relation, successorLateStart);
         }

         // successor started
         if (successorTask.actual_finish == null) {
            // successor not finished
            if (relation.lag == 0) {
               return this.removeLag(relation, successorLateStart);
            }

            if (relation.lag > 0) {
               return this.removeLag(relation, successorLateStart);
            }

            return this.removeLag(relation, successorLateStart);
         }

         // successor finished
         if (relation.lag == 0) {
            return successorLateStart;
         }

         if (relation.lag > 0) {
            return successorLateStart;
         }

         return successorLateStart;
      }

      // Predecessor Started
      if (predecessorTask.actual_finish != null) {
         const actualFinish = DateUtils.parseISO(predecessorTask.actual_finish);
         // Predecessor finished
         if (successorTask.actual_start == null) {
            // Successor not started
            if (relation.lag == 0) {
               return successorLateStart;
            }

            if (relation.lag > 0) {
               const actualLagDurationInHours = actualFinish > this.m_dataDate ? 0 : (this.getLagCalendar(relation).getWork(actualFinish, this.m_dataDate) || 0) / DateUtils.SECONDS_PER_HOUR;
               const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;

               if (lagDurationInHours > actualLagDurationInHours) {
                  const remainingLag = (lagDurationInHours - actualLagDurationInHours) * DateUtils.SECONDS_PER_HOUR;
                  return this.removeLag_x(relation, successorLateStart, remainingLag);
               }

               return successorLateStart;
            }

            return successorLateStart;
         }

         // successor started
         if (successorTask.actual_finish == null) {
            // check for actual progress
            if (relation.lag == 0) {
               return successorLateStart;
            }

            if (relation.lag > 0) {
               if ((successorTask.actual_duration || 0) === 0) {
                  return this.removeLag(relation, successorLateStart);
               }

               return successorLateStart;
            }

            if ((successorTask.actual_duration || 0) === 0) {
               return this.removeLag(relation, successorLateStart);
            }

            return successorLateStart;
         }

         // successor finished
         if (relation.lag == 0) {
            return successorLateStart;
         }

         if (relation.lag > 0) {
            const actualLagDurationInHours = actualFinish > this.m_dataDate ? 0 : (this.getLagCalendar(relation).getWork(actualFinish, this.m_dataDate) || 0) / DateUtils.SECONDS_PER_HOUR;
            const lagDurationInHours = (relation.lag || 0) / DateUtils.SECONDS_PER_HOUR;

            if (lagDurationInHours > actualLagDurationInHours) {
               const remainingLag = (lagDurationInHours - actualLagDurationInHours) * DateUtils.SECONDS_PER_HOUR;
               return this.removeLag_x(relation, successorLateStart, remainingLag);
            }

            return successorLateStart;
         }

         return successorLateStart;
      }

      // Predecessor not finished
      if (successorTask.actual_start == null) {
         // Successor not started
         if (relation.lag == 0) {
            return this.removeLag(relation, successorLateStart);
         }

         if (relation.lag > 0) {
            return this.removeLag(relation, successorLateStart);
         }

         return this.removeLag(relation, successorLateStart);
      }
      // successor started
      if (successorTask.actual_finish == null) {
         // check for actual progress
         if (relation.lag == 0) {
            if ((successorTask.actual_duration || 0) === 0) {
               return this.removeLag(relation, successorLateStart);
            }

            return successorLateStart;
         }

         if (relation.lag > 0) {
            if ((successorTask.actual_duration || 0) === 0) {
               return this.removeLag(relation, successorLateStart);
            }

            return successorLateStart;
         }

         if ((successorTask.actual_duration || 0) === 0) {
            return this.removeLag(relation, successorLateStart);
         }

         return successorLateStart;
      }

      // successor finished
      if (relation.lag == 0) {
         return successorLateStart;
      }

      if (relation.lag > 0) {
         return successorLateStart;
      }

      return successorLateStart;
   }



   /**
    * Ensure a late finish date is before the project finish date, and adjust if required.
    *
    * @param relation parent relation
    * @param lateFinish late finish date
    * @return late finish
    */
   private adjustLateFinish(relation: TaskRelationship, lateFinish: Date): Date {
      if (lateFinish > this.m_projectFinishDate) {
         // If we're between working periods, move back to the last work finish
         lateFinish = this.getEquivalentPreviousWorkFinish(this.getPredecessorTask(relation), this.m_projectFinishDate);
      }

      return lateFinish;
   }



   /**
 * Perform the CPM backward pass.
 *
 * @param forwardPassTasks tasks in order for forward pass
 */
   private backwardPass(tasks: TaskDoc[]) {
      const reversedTasks = tasks.reverse();
      for (const task of reversedTasks) {
         this.backwardPassTask(task);
      }
   }

   /**
    * Perform the CPM backward pass for this task.
    *
    * @param task task to schedule
    */
   private backwardPassTask(task: TaskDoc) {
      const successors = this.filterSuccessors(task.successors);
      let lateFinish: Date | null = null;
      const constraintDate = DateUtils.parseISO(task.constraint_date);

      if (task.actual_finish == null) {
         if (successors.length === 0) {
            if (task.external_late_finish == null) {
               if (this.m_schedule.schedule.property_values.must_finish_by != null) {
                  lateFinish = DateUtils.parseISO(this.m_schedule.schedule.property_values.must_finish_by);
               } else {
                  lateFinish = this.m_projectFinishDate;
               }
            } else {
               lateFinish = DateUtils.parseISO(task.external_late_finish);
            }
         } else {
            lateFinish = this.getMinSuccessorLateFinish(successors);
         }

         switch (task.constraint_type) {
            case ConstraintType.START_ON:
               {
                  if (task.actual_start == null) {
                     const latestFinish = this.getDateFromStartAndDuration(task, constraintDate);
                     if (lateFinish && lateFinish > latestFinish) {
                        lateFinish = latestFinish;
                     }
                  }
                  break;
               }

            case ConstraintType.MUST_START_ON:
               {
                  if (task.actual_start == null) {
                     lateFinish = this.getDateFromStartAndDuration(task, constraintDate);
                  }
                  break;
               }

            case ConstraintType.MUST_FINISH_ON:
               {
                  lateFinish = constraintDate;
                  break;
               }

            case ConstraintType.START_NO_LATER_THAN:
               {
                  const latestFinish = this.getDateFromStartAndDuration(task, constraintDate);
                  if (lateFinish && lateFinish > latestFinish) {
                     lateFinish = latestFinish;
                  }
                  break;
               }

            case ConstraintType.FINISH_ON:
            case ConstraintType.FINISH_NO_LATER_THAN:
               {
                  if (lateFinish && lateFinish > constraintDate) {
                     lateFinish = constraintDate;
                  }
                  break;
               }

            default:
               {
                  break;
               }
         }

         if (task.secondary_constraint_type != null) {
            const secondaryConstraintDate = DateUtils.parseISO(task.secondary_constraint_date);
            switch (task.secondary_constraint_type) {
               case ConstraintType.START_NO_LATER_THAN:
                  {
                     const latestFinish = this.getDateFromStartAndDuration(task, secondaryConstraintDate);
                     if (lateFinish && lateFinish > latestFinish) {
                        lateFinish = latestFinish;
                     }
                     break;
                  }

               case ConstraintType.FINISH_NO_LATER_THAN:
                  {
                     if (lateFinish && lateFinish > secondaryConstraintDate) {
                        lateFinish = secondaryConstraintDate;
                     }
                     break;
                  }

               default:
                  {
                     break;
                  }
            }
         }

         // If we are at the start of the next period of work, we can move back to the end of the previous period of work
         lateFinish = this.getEquivalentPreviousWorkFinish(task, lateFinish);
      }
      else {
         if (successors.length === 0) {
            if (this.m_schedule.schedule.property_values.must_finish_by != null) {
               lateFinish = DateUtils.parseISO(this.m_schedule.schedule.property_values.must_finish_by);
            } else {
               lateFinish = this.m_projectFinishDate;
            }
         } else {
            lateFinish = this.getMinSuccessorLateFinish(successors);
         }
      }

      const externalLateFinish = DateUtils.parseISO(task.external_late_finish);
      if (externalLateFinish != null && externalLateFinish < lateFinish) {
         lateFinish = externalLateFinish;
      }

      let lateStart = this.getDateFromFinishAndRemainingDuration(task, lateFinish);
      if (task.activity_type == ActivityType.START_MILESTONE) {
         lateStart = this.getNextWorkStart(task, lateStart);
      } else {
         if (task.activity_type != ActivityType.FINISH_MILESTONE && this.hasRemainingDuration(task)) {
            lateStart = this.getNextWorkStart(task, lateStart);
         }
      }

      task.late_start = DateUtils.toIso(lateStart);
      task.late_finish = DateUtils.toIso(lateFinish);
      this.setRemainingLateDates(task);

      if (task.constraint_type == ConstraintType.FINISH_NO_LATER_THAN) {
         this.alapAdjust(task);
      }
   }

   /**
* Populate the remaining late dates.
*
* @param task target task
*/
   private setRemainingLateDates(task: TaskDoc) {
      if (task.actual_finish != null) {
         return;
      }

      task.remaining_late_start = task.late_start;
      task.remaining_late_finish = task.late_finish;
   }

   /**
 * Adjust Early Start and Early Finish dates for an activity with
 * an "As Late As Possible" constraint.
 *
 * @param task ALAP constrained task
 */
   private alapAdjust(task: TaskDoc) {
      let earlyStart: Date | null = null;
      let earlyFinish: Date | null = null;
      // Filter relations to only include those where the successor is an activity
      // Calculate early start for each relation and find the earliest one
      const activityRelations = (task.successors || []).filter(rel => {
         const successorTask = this.getSuccessorTask(rel);
         return successorTask != null && ScheduleCalculate.isActivity(successorTask);
      }).map(rel => ({ relation: rel, earlyStart: this.getAlapEarlyStart(rel) }))
         .filter(item => item.earlyStart != null);

      if (activityRelations.length === 0) {
         earlyFinish = this.m_projectFinishDate;
         earlyStart = this.getDateFromFinishAndRemainingDuration(task, earlyFinish);
      } else {
         // Find the relation with the earliest early start
         const earliestRelation = activityRelations.reduce((earliest, current) =>
            current.earlyStart < earliest.earlyStart ? current : earliest
         );
         earlyStart = earliestRelation.earlyStart;
         earlyFinish = this.getDateFromStartAndRemainingDuration(task, earlyStart);
      }

      task.early_start = DateUtils.toIso(earlyStart);
      task.early_finish = DateUtils.toIso(earlyFinish);
      // TODO: Implement setRemainingEarlyDates if needed
   }

   /**
 * Calculate the ALAP Early Start from a relation's successor task.
 *
 * @param relation target relation
 * @return early start date
 */
   private getAlapEarlyStart(relation: TaskRelationship): Date {
      const predecessorTask = this.getPredecessorTask(relation);
      const successorTask = this.getSuccessorTask(relation);
      const successorEarlyStart = DateUtils.parseISO(successorTask.early_start);


      switch (relation.type) {
         case RelationshipType.START_START:
            {
               return this.removeLag(relation, successorEarlyStart);
            }

         case RelationshipType.FINISH_START:
            {
               if (predecessorTask.actual_start == null) {
                  // Predecessor not started
                  if (successorTask.actual_start == null) {
                     // Successor not started
                     return this.removeLag(relation, this.getDateFromFinishAndRemainingDuration(predecessorTask, successorEarlyStart));
                  }

                  // successor started
                  if (successorTask.actual_finish == null) {
                     // successor not finished
                     return this.removeLag(relation, this.getDateFromFinishAndRemainingDuration(predecessorTask, successorEarlyStart));
                  }

                  // successor finished
                  return this.removeLag(relation, this.getDateFromFinishAndRemainingDuration(predecessorTask, successorEarlyStart));
               }

               // Predecessor Started
               if (predecessorTask.actual_finish == null) {
                  // Predecessor not finished
                  if (successorTask.actual_start == null) {
                     // Successor not started
                     return this.removeLag(relation, this.getDateFromFinishAndRemainingDuration(predecessorTask, successorEarlyStart));
                  }

                  // successor started
                  if (successorTask.actual_finish == null) {
                     // successor not finished
                     return this.removeLag(relation, this.getDateFromFinishAndRemainingDuration(predecessorTask, successorEarlyStart));
                  }

                  // successor finished
                  return this.removeLag(relation, this.getDateFromFinishAndRemainingDuration(predecessorTask, successorEarlyStart));
               }

               // Predecessor finished
               if (successorTask.actual_start == null) {
                  // Successor not started
                  return this.getEquivalentNextWorkStart(predecessorTask, this.m_dataDate);
               }

               // successor started
               if (successorTask.actual_finish == null) {
                  // successor not finished
                  return this.getEquivalentNextWorkStart(predecessorTask, this.m_dataDate);
               }

               // successor finished
               return this.m_dataDate;
            }

         case RelationshipType.FINISH_FINISH:
            {
               const earlyFinish = this.removeLag(relation, DateUtils.parseISO(successorTask.early_finish));
               return this.getDateFromFinishAndRemainingDuration(predecessorTask, earlyFinish);
            }

         default:
            {
               // TODO: need example to determine correct behaviour here
               return this.m_projectFinishDate;
            }
      }
   }

   private removeLag_x(relation: TaskRelationship, date: Date, lag: number): Date {
      if (date == null) {
         return null;
      }

      return this.getLagCalendar(relation).getDate(date, -lag);
   }
   private removeLag(relation: TaskRelationship, date: Date): Date {
      if (date == null) {
         return null;
      }

      return this.getLagCalendar(relation).getDate(date, relation.lag);
   }

   private getEquivalentNextWorkStart(task: TaskDoc, date: Date): Date {
      const calendar = this.getEffectiveCalendar(task);
      const adjustedDate = calendar.getNextWorkStart(date);
      if (calendar.getWork(date, adjustedDate) == 0) {
         return adjustedDate;
      }
      return date;
   }

   private updateDates(tasks: TaskDoc[]): void {
      tasks.forEach(task => this.updateDatesTask(task));
   }

   /**
    * Update Start, Finish, Planned Start, and Planned Finish dates.
    *
    * @param activity activity to update
    */
   private updateDatesTask(task: TaskDoc): void {
      const original: TaskDoc = { ...task };
      task.start = task.actual_start == null ? task.early_start : task.actual_start;
      task.finish = task.actual_finish == null ? task.early_finish : task.actual_finish;

      // P6 moves the planned start/finish dates for unstarted activities
      if (task.actual_start == null) {
         task.planned_start = task.start;
         task.planned_finish = task.finish;
      }
      //activity.getResourceAssignments().forEach(this::updateDates);

      this.registerDeltas(original, task);
   }

   /**
    * My method to clear dates for the preview run.
    */
   private clearContainerDates(parentTask: TaskDoc) {
      const childTasks = this.getChildTasks(parentTask);
      if (childTasks.length == 0) {
         return;
      }
      this.clearTaskDates(parentTask);
      childTasks.forEach(task => this.clearContainerDates(task));
   }

   /**
   * Roll up dates to a WBS entity.
   *
   * @param parentTask parent WBS
   */
   private rollupDatesTask(parentTask: TaskDoc): void {
      // NOTE: LOE and WBS Summary are ignore at this point as they have null dates
      const childTasks = this.getChildTasks(parentTask);
      if (childTasks.length == 0) {
         return;
      }

      if (parentTask.name == 'Doors') {
         console.log('childTasks', childTasks.map(task => task.name));
      }
      console.log('rollupDatesTask', parentTask.name);

      // Store original values to detect changes
      const original: TaskDoc = { ...parentTask };
      let maxDate = new Date(8640000000000000).toISOString();
      let minDate = new Date(-8640000000000000).toISOString();

      let startDate = maxDate;
      let finishDate = minDate;//this.m_projectStartDate;//parentTask.finish;
      let earlyStartDate = maxDate;// parentTask.early_start;
      let earlyFinishDate = minDate;// parentTask.early_finish;
      let lateStartDate = maxDate;// parentTask.late_start;
      let lateFinishDate = minDate;// parentTask.late_finish;
      let remainingEarlyStartDate = maxDate;// parentTask.remaining_early_start;
      let remainingEarlyFinishDate = minDate;// parentTask.remaining_early_finish;
      let remainingLateStartDate = maxDate;// parentTask.remaining_late_start;
      let remainingLateFinishDate = minDate;// parentTask.remaining_late_finish;

      let plannedStartDate = parentTask.planned_start;
      let plannedFinishDate = parentTask.planned_finish;
      let actualStartDate = parentTask.actual_start;
      let actualFinishDate = parentTask.actual_finish;
      let baselineStartDate = parentTask.baseline_start;
      let baselineFinishDate = parentTask.baseline_finish;

      let finished = 0;
      let critical = false;

      for (const task of childTasks) {
         // Collect modified tasks from recursive calls
         this.rollupDatesTask(task);

         // the child tasks can have null dates (e.g. for nested wbs elements with no task children) so we
         // still must protect against some children having null dates

         startDate = DateUtils.min(startDate, task.start);
         finishDate = DateUtils.max(finishDate, task.finish);
         plannedStartDate = DateUtils.min(plannedStartDate, task.planned_start);
         plannedFinishDate = DateUtils.max(plannedFinishDate, task.planned_finish);
         actualStartDate = DateUtils.min(actualStartDate, task.actual_start);
         actualFinishDate = DateUtils.max(actualFinishDate, task.actual_finish);
         baselineStartDate = DateUtils.min(baselineStartDate, task.baseline_start);
         baselineFinishDate = DateUtils.max(baselineFinishDate, task.baseline_finish);

         if (task.activity_type != ActivityType.LEVEL_OF_EFFORT || task.actual_finish == null) {
            earlyStartDate = DateUtils.min(earlyStartDate, task.early_start);
            earlyFinishDate = DateUtils.max(earlyFinishDate, task.early_finish);
            remainingEarlyStartDate = DateUtils.min(remainingEarlyStartDate, task.remaining_early_start);
            remainingEarlyFinishDate = DateUtils.max(remainingEarlyFinishDate, task.remaining_early_finish);
            lateStartDate = DateUtils.min(lateStartDate, task.late_start);
            lateFinishDate = DateUtils.max(lateFinishDate, task.late_finish);
            remainingLateStartDate = DateUtils.min(remainingLateStartDate, task.remaining_late_start);
            remainingLateFinishDate = DateUtils.max(remainingLateFinishDate, task.remaining_late_finish);
         }

         if (task.actual_finish != null) {
            ++finished;
         }

         critical = critical || task.critical;
      }

      parentTask.start = startDate;
      parentTask.finish = finishDate;
      parentTask.planned_start = plannedStartDate;
      parentTask.planned_finish = plannedFinishDate;
      parentTask.actual_start = actualStartDate;
      parentTask.early_start = earlyStartDate;
      parentTask.early_finish = earlyFinishDate;
      parentTask.remaining_early_start = remainingEarlyStartDate;
      parentTask.remaining_early_finish = remainingEarlyFinishDate;
      parentTask.late_start = lateStartDate;
      parentTask.late_finish = lateFinishDate;
      parentTask.remaining_late_start = remainingLateStartDate;
      parentTask.remaining_late_finish = remainingLateFinishDate;
      parentTask.baseline_start = baselineStartDate;
      parentTask.baseline_finish = baselineFinishDate;

      //
      // Only if all child tasks have actual finish dates do we
      // set the actual finish date on the parent task.
      //
      if (finished == childTasks.length) {
         parentTask.actual_finish = actualFinishDate;
      }

      let plannedDuration = null;
      if (plannedStartDate != null && plannedFinishDate != null) {
         // First Container: 2026-01-01T16:40, 2026-02-20T16:40 => 343h
         plannedDuration = this.getEffectiveCalendar(parentTask).calculateDuration(DateUtils.parseISO(plannedStartDate), DateUtils.parseISO(plannedFinishDate));
         parentTask.planned_duration = plannedDuration;
      }

      let actualDuration: number = null;
      let remainingDuration: number = null;
      if (parentTask.actual_finish == null) {
         let taskStartDate = parentTask.remaining_early_start;
         if (taskStartDate == null) {
            taskStartDate = parentTask.early_start;
            if (taskStartDate == null) {
               taskStartDate = plannedStartDate;
            }
         }

         let taskFinishDate = parentTask.remaining_early_finish;
         if (taskFinishDate == null) {
            taskFinishDate = parentTask.early_finish;
            if (taskFinishDate == null) {
               taskFinishDate = plannedFinishDate;
            }
         }

         if (taskStartDate != null) {
            if (parentTask.actual_start != null) {
               actualDuration = this.getEffectiveCalendar(parentTask).calculateDuration(DateUtils.parseISO(parentTask.actual_start), DateUtils.parseISO(taskStartDate));
            }

            if (taskFinishDate != null) {
               remainingDuration = this.getEffectiveCalendar(parentTask).calculateDuration(DateUtils.parseISO(taskStartDate), DateUtils.parseISO(taskFinishDate));
            }
         }
      } else {
         actualDuration = this.getEffectiveCalendar(parentTask).calculateDuration(DateUtils.parseISO(parentTask.actual_start), DateUtils.parseISO(parentTask.actual_finish));
         remainingDuration = 0;
      }

      if (actualDuration != null && actualDuration < 0) {
         actualDuration = null;
      }

      if (remainingDuration != null && remainingDuration < 0) {
         remainingDuration = null;
      }

      parentTask.actual_duration = actualDuration;
      parentTask.remaining_duration = remainingDuration;
      parentTask.duration = actualDuration + remainingDuration;

      if (plannedDuration != null && remainingDuration != null && plannedDuration != 0) {
         let durationPercentComplete: number = ((plannedDuration - remainingDuration) / plannedDuration) * 100.0;
         if (durationPercentComplete < 0.0) {
            durationPercentComplete = 0;
         } else {
            if (durationPercentComplete > 100) {
               durationPercentComplete = 100;
            }
         }
         parentTask.percent_complete = durationPercentComplete;
         parentTask.percent_complete_type = PercentCompleteType.DURATION;
      }

      // Force total slack calculation to avoid overwriting the critical flag
      // parentTask.getTotalSlack(); unsure how this forces total slack calculation
      parentTask.critical = critical;

      this.registerDeltas(original, parentTask);
   }

   private registerDeltas(original: TaskDoc, modified: TaskDoc): void {
      for (const key of Object.keys(modified)) {
         // Exclude cases where one is null and the other is undefined
         if (modified[key] !== original[key] && !(modified[key] == null && original[key] == null)) {
            this.registerDelta(modified.unique_id, {
               field: key,
               old_value: original[key],
               new_value: modified[key],
            });
         }
      }
   }


   private registerDelta(unique_id: number, delta: FieldDelta): void {
      let existing = this.taskDeltas.get(unique_id) || [];
      this.taskDeltas.set(unique_id, [...existing, delta]);
   }



   /**
 * Schedule WBS Summary activities.
 */
   private wbsSummaryPass(): void {
      const activities = this.m_schedule.tasks.filter(this.isWbsSummary);
      if (activities.length === 0) {
         return;
      }

      for (const activity of activities) {
         this.wbsSummaryPassTask(activity);
      }
   }

   private isWbsSummary(task: TaskDoc): boolean {
      return task.activity_type == ActivityType.WBS_SUMMARY;
   }


   private getParentTask(task: TaskDoc): TaskDoc | null {
      return task.parent_task_unique_id ? this.getTask(task.parent_task_unique_id) : null;
   }

   private getChildTasks(task: TaskDoc): TaskDoc[] {
      return this.m_schedule.tasks.filter(t => t.parent_task_unique_id === task.unique_id);
   }

   /**
    * Descend through the entire hierarchy beneath a WBS entry collecting all child tasks.
    *
    * @param wbs WBS entry
    * @param childTasks list to populated with child tasks
    * @return list of child tasks
    */
   private getAllWbsDecendents(wbs: TaskDoc): TaskDoc[] {
      const wbsChildTasks = this.getChildTasks(wbs);
      // Recursively collect from all child tasks and combine with current level
      const allChildTasks = [...wbsChildTasks.filter(t => !t.summary && t.activity_type != ActivityType.WBS_SUMMARY && t.actual_finish == null)];
      for (const childTask of wbsChildTasks) {
         allChildTasks.push(...this.getAllWbsDecendents(childTask));
      }

      return allChildTasks;
   }

   private wbsSummaryPassTask(task: TaskDoc): void {
      const parent = this.getParentTask(task);
      if (parent == null) {
         return;
      }

      // These values can be used directly from the parent WBS
      task.start = parent.start;
      task.finish = parent.finish;
      task.actual_start = parent.actual_start;
      task.actual_finish = parent.actual_finish;

      // These values can only be rolled up from not started or in progress child activities of the WBS
      // so we need to descend the wbs hierarchy find all child activities which match these criteria.
      const childTasks = this.getAllWbsDecendents(parent);
      // Calculate min early start from child tasks
      task.early_start = DateUtils.toIso(this.getMinTaskDate(childTasks, 'early_start'));

      // Calculate max early finish from child tasks
      task.early_finish = DateUtils.toIso(this.getMaxTaskDate(childTasks, 'early_finish'));

      // Calculate min remaining early start from child tasks
      task.remaining_early_start = DateUtils.toIso(this.getMinTaskDate(childTasks, 'remaining_early_start'));

      // Calculate max remaining early finish from child tasks
      task.remaining_early_finish = DateUtils.toIso(this.getMaxTaskDate(childTasks, 'remaining_early_finish'));

      // Calculate min late start from child tasks
      task.late_start = DateUtils.toIso(this.getMinTaskDate(childTasks, 'late_start'));

      // Calculate max late finish from child tasks
      task.late_finish = DateUtils.toIso(this.getMaxTaskDate(childTasks, 'late_finish'));

      // Calculate min remaining late start from child tasks
      task.remaining_late_start = DateUtils.toIso(this.getMinTaskDate(childTasks, 'remaining_late_start'));

      // Calculate max remaining late finish from child tasks
      task.remaining_late_finish = DateUtils.toIso(this.getMaxTaskDate(childTasks, 'remaining_late_finish'));
   }

   private getMinTaskDate(tasks: TaskDoc[], dateField: string): Date {
      return tasks.filter(t => t[dateField] != null).map(t => DateUtils.parseISO(t[dateField])).reduce((a, b) => a < b ? a : b, null);
   }

   private getMaxTaskDate(tasks: TaskDoc[], dateField: string): Date {
      return tasks.filter(t => t[dateField] != null).map(t => DateUtils.parseISO(t[dateField])).reduce((a, b) => a > b ? a : b, null);
   }


   private debugDates(task: TaskDoc): void {
      var debugString = '==============================================\n';
      debugString += `debugDates ${task.name}\n`;
      debugString += `start ${task.start}\n`;
      debugString += `finish ${task.finish}\n`;
      debugString += `actual_start ${task.actual_start}\n`;
      debugString += `actual_finish ${task.actual_finish}\n`;
      debugString += `planned_start ${task.planned_start}\n`;
      debugString += `planned_finish ${task.planned_finish}\n`;
      debugString += `early_start ${task.early_start}\n`;
      debugString += `early_finish ${task.early_finish}\n`;
      debugString += `remaining_early_start ${task.remaining_early_start}\n`;
      debugString += `remaining_early_finish ${task.remaining_early_finish}\n`;
      debugString += `late_start ${task.late_start}\n`;
      debugString += `late_finish ${task.late_finish}\n`;
      debugString += `remaining_late_start ${task.remaining_late_start}\n`;
      debugString += `remaining_late_finish ${task.remaining_late_finish}\n`;
      console.log(debugString);
   }
}
